"use client";
import { ItemType } from "@/app/integrations/[item]/page";
import CommonTitle from "../common/CommonTitle";
import Image from "next/image";
import { SignUpHubspotButtonUrl } from "@/utils/hubspot-contants";
import CommonSubtitle from "../common/CommonSubtitle";
import CustomButton from "../common/CustomButton";
import keyFeatureGrid1 from "@/assets/images/integrations/keyFeatures/grid-1.svg";
import keyFeatureGrid2 from "@/assets/images/integrations/keyFeatures/grid-2.svg";
import keyFeatureGrid3 from "@/assets/images/integrations/keyFeatures/grid-3.svg";
import keyFeatureGrid4 from "@/assets/images/integrations/keyFeatures/grid-4.svg";
import keyFeatureGrid5 from "@/assets/images/integrations/keyFeatures/grid-5.svg";
import keyFeatureGrid6 from "@/assets/images/integrations/keyFeatures/grid-6.svg";
import keyFeatureGrid7 from "@/assets/images/integrations/keyFeatures/grid-7.svg";
import keyFeatureGrid8 from "@/assets/images/integrations/keyFeatures/grid-8.svg";
import keyFeatureGrid9 from "@/assets/images/integrations/keyFeatures/grid-9.svg";

const keyFeatureGrid = [
  keyFeatureGrid1,
  keyFeatureGrid2,
  keyFeatureGrid3,
  keyFeatureGrid4,
  keyFeatureGrid5,
  keyFeatureGrid6,
  keyFeatureGrid7,
  keyFeatureGrid8,
  keyFeatureGrid9,
];


const StepSection = ({ itemInfo }: { itemInfo: ItemType }) => {
  return (
    <>
      <section className="pb-[60px]">
        <div className="bg-[#F6F8FE]">
          <div className="container">
            <div className="w-full space-y-8 py-6 sm:py-8 xl:py-16">
              <div className="space-y-8 sm:space-y-10">
                <div className="text-center">
                  <CommonTitle
                    as="h2"
                    fontWeightClass="font-bold"
                    color="#202020"
                    fontSizeClass="text-[24px] lg:text-[32px]"
                  >
                    Steps to Integrate <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">{itemInfo.itemName} with Kroolo</span>
                  </CommonTitle>
                </div>
                <div className="flex flex-wrap justify-center gap-6 lg:gap-8 xl:gap-10">
                  {itemInfo.stepsIntegration?.stepsData.map((step) => (
                    <div
                      key={step.id}
                      className={`flex min-w-[140px] max-w-[200px] flex-1 flex-col items-center text-center ${step.id % 2 === 0 ? "mt-20" : "mt-0"}`}
                    >
                      <div className="mb-3 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#C4B5FD] bg-white">
                        <span className="text-[22px] font-bold text-[#202020]">
                          {step.id}
                        </span>
                      </div>
                      <h3 className="mb-2 text-[14px] font-bold leading-tight lg:text-[16px] bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                        {step.stepsTitle}
                      </h3>
                      <div
                        className="text-[12px] font-normal leading-relaxed text-[#717171] lg:text-[14px]"
                        dangerouslySetInnerHTML={{
                          __html: step.stepsDescription,
                        }}
                      />
                    </div>
                  ))}
                </div>
                <p className="w-fit mx-auto flex flex-wrap items-center justify-center gap-1.5 text-center text-[18px] font-normal text-[#202020] lg:text-[20px] bg-white rounded-lg px-4 py-2 border border-[#E5E5E5]">
                  For detailed guidance, visit our&nbsp;
                  <a
                    href="https://help.kroolo.com/"
                    className="inline-flex items-center justify-center rounded-lg bg-[#202020] px-4 py-2.5 text-[16px] font-medium text-white transition-colors hover:bg-[#333333]"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Help Center
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
        <section className="bg-white py-12 xl:py-20">
          <div className="container mx-auto px-4">
            <div className="mb-8 text-center xl:mb-10">
              <div className="mx-auto mb-2 inline-flex w-fit rounded-full bg-[#f3f3f3] px-3 py-1 text-sm font-semibold">
                <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                  Integration
                </span>
              </div>
              <CommonTitle
                as="h2"
                color="#202020"
                fontSizeClass="text-[24px] lg:text-[32px]"
                fontWeightClass="font-semibold"
                maxWidthClass="mx-auto max-w-6xl"
              >
                Key Features of {itemInfo.itemName} - Kroolo Integration
              </CommonTitle>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {itemInfo.benefitSteps?.stepsData.map((feature, index) => (
                <div
                  key={index}
                  className="rounded-2xl bg-gradient-to-b from-[#F4F1FF] to-white p-4 xl:p-6"
                >
                  <CommonTitle
                    as="h3"
                    color="#202020"
                    fontSizeClass="text-[18px] lg:text-[20px] mb-2"
                    fontWeightClass="font-semibold"
                  >
                    {feature.stepsTitle}
                  </CommonTitle>
                  <CommonSubtitle fontSizeWeight="mb-6 min-h-[54px]">
                    {feature.stepsDescription}
                  </CommonSubtitle>
                  <div className="relative w-full h-auto">
                    <Image
                      src={feature.image || keyFeatureGrid[index]}
                      alt={`Key Feature Grid ${index + 1}`}
                      width={100}
                      height={100}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
              ))}
              <div
                className="rounded-2xl bg-gradient-to-b from-[#F4F1FF] to-white p-4 xl:p-6"
              >
                <div className="relative w-full h-[200px]">
                  <Image src={itemInfo.roleSteps?.image || ""} alt={`${itemInfo.itemName} - Integration setup steps`} width={100} height={100} className="object-contain w-full h-full" />
                </div>
                <CommonSubtitle fontSizeWeight="mb-6 min-h-[54px]">
                  {itemInfo.benefitSteps?.description}
                </CommonSubtitle>
                <div className="mt-2 w-fit">
                  <CustomButton
                    text="Integrate With Kroolo"
                    variant="primary"
                    version="black"
                    url={SignUpHubspotButtonUrl}
                  />
                </div>

              </div>
            </div>
          </div>
        </section>
      </section>
    </>
  );
};

export default StepSection;
